import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { DeliveryMethod, VisitDtoModelType } from '../../../../store/mst/models';
import AddressRow from './AddressRow';
import PhoneNumberRow from './PhoneNumberRow';
import { DetailsCard } from '../../../shared/DetailsCard';
import { filterRows } from './utils';
import CustomerProfileBadge from '../../../customerProfiles/CustomerProfileBadge';

interface CustomerDetailsSectionOptions {
  canEditVisits: boolean;
  deliveryMethod?: DeliveryMethod;
}

interface Props {
  visit: VisitDtoModelType;
  orderDetailsOptions: CustomerDetailsSectionOptions;
  onOpenCustomerProfile?: (source: HTMLElement) => void;
}

const CustomerDetailsSection: React.FC<Props> = ({ visit, orderDetailsOptions, onOpenCustomerProfile }) => {
  const { t } = useTranslation('common');
  const { canEditVisits, deliveryMethod } = orderDetailsOptions;

  const allRows: { label: string; value: React.ReactNode }[] = [
    {
      label: t('orderDetails.name'),
      value: visit.customerId
        ? <CustomerProfileBadge name={visit.name} onOpen={onOpenCustomerProfile} />
        : visit.name?.trim() || undefined,
    },
    {
      label: t('orderDetails.address'),
      value: visit.displayLocation
        ? <AddressRow visit={visit} canEditVisits={canEditVisits} />
        : undefined,
    },
    {
      label: t('orderDetails.phone'),
      value: visit.phone?.trim()
        ? <PhoneNumberRow phone={visit.phone} deliveryMethod={deliveryMethod} />
        : undefined,
    },
    { label: t('orderDetails.email'), value: visit.email?.trim() || undefined },
  ];
  const rows = filterRows(allRows);

  if (rows.length === 0) {
    return null;
  }

  return (
    <DetailsCard
      data-testid="order-details-card"
      header={t('orderDetails.customer')}
      rows={rows}
    />
  );
};

export default observer(CustomerDetailsSection);
